import type { JSX } from 'react';
import { useState } from 'react';
import type { PatientSummary } from '@fatigue/shared';
import { resetPatientAccess } from '../data/api';

type State = 'idle' | 'confirming' | 'sending' | 'sent';

/**
 * Restore a patient's access: they get an email with a link to choose a new
 * password, and the app signs out on every phone that was using the old one.
 *
 * The only way back in for a patient — the patient pool has no "forgot
 * password", because a reset that starts at the patient's inbox is an
 * unauthenticated route into their record. Here it starts with somebody who
 * knows them, which is the point.
 *
 * One confirmation step, because signing a patient out everywhere is
 * disruptive if pressed by mistake, and the copy says so.
 */
export function ResetAccess({ patient }: { readonly patient: PatientSummary }): JSX.Element {
  const [state, setState] = useState<State>('idle');
  const [error, setError] = useState<string | null>(null);

  const send = (): void => {
    if (state === 'sending') return;
    setState('sending');
    setError(null);
    resetPatientAccess(patient.patientId)
      .then(() => setState('sent'))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Could not send the reset email.');
        setState('confirming');
      });
  };

  if (state === 'idle') {
    return (
      <button type="button" className="ghost reset__open" onClick={() => setState('confirming')}>
        Reset access
      </button>
    );
  }

  if (state === 'sent') {
    return (
      <p className="reset__done" role="status">
        Sent. {patient.displayName} has a link to choose a new password; it works once, for seven
        days.
      </p>
    );
  }

  return (
    <div className="reset__confirm">
      <p>
        Email {patient.displayName} a link to choose a new password? Their app will sign out on
        every phone until they sign in with it. Their entries are not affected.
      </p>
      {error !== null && (
        <p className="admin__error" role="alert">
          {error}
        </p>
      )}
      <div className="reset__actions">
        <button type="button" className="primary" disabled={state === 'sending'} onClick={send}>
          {state === 'sending' ? 'Sending…' : 'Send link'}
        </button>
        <button
          type="button"
          className="ghost"
          disabled={state === 'sending'}
          onClick={() => {
            setState('idle');
            setError(null);
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
